import React, { useCallback, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { stringLength } from '~/utils/helperFunctions';
import { displayTooltip } from '~/hooks/displayTooltip';
import useScreenWidth from '~/hooks/useScreenWidth';

// Actions
import { deleteTemplate, getAllTemplates, getViewProof } from '~/redux/actions/template-builder';
import useSortParams from '~/hooks/useSortParams';
import { failure, success } from '~/redux/actions/snackbar-actions';

// Utils
import { dateFormat } from '~/utils/date-format';
import { getNoResultsMessage, MESSAGES } from '~/utils/message';
import { createEnvelopeProof } from '~/utils/template-builder';
import { HAS_ENVELOPE } from '~/utils/constants';

// Components
import DeleteTemplateModal from '~/components/Templates/DeleteModal';
import DuplicateTemplateModal from '~/components/Templates/DuplicateModal';
import ConfirmModal from '~/components/General/ConfirmModal';
import OlcActionMenu from '~/components/General/OlcActionMenu';

// Icons
import {
  TemplateSearch2CIcon,
  TemplateDeleteCIcon,
  TemplateCopyCIcon,
  DownloadTemplateCIcon,
  DuplicateTemplateCIcon,
  TrashTemplateCIcon,
  ContactDetailsDeleteCIcon,
} from '~/assets/images';
import MailIcon from '@mui/icons-material/MailOutlined';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import { Box, MenuItem, Typography, Tooltip, CircularProgress, IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SaveAltIcon from '@mui/icons-material/SaveAlt';

// styles
import './styles.scss';

/**
 * TemplateListing Component
 * Displays a list of templates with various actions.
 * @returns {JSX.Element} The data grid component showing list templates.
 */
const TemplateListing = (props) => {
  const {
    selectedTemplateIds,
    searchApplied,
    filterSize,
    totalTemplatesInDB,
    isLoading,
    onSortChange,
  } = props;
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isShowDeleteModel, setIsShowDeleteModel] = useState({
    open: false,
    type: 'delete',
  });
  const [selectedRecord, setSelectedRecord] = useState({});
  const search = useSelector((state) => state.templateReducer.search);
  const searchProductIds = useSelector((state) => state.templateReducer.searchProductIds);
  const searchCreator = useSelector((state) => state.templateReducer.searchCreator);
  const sortBy = useSelector((state) => state.templateReducer.sortBy);
  const sortOrder = useSelector((state) => state.templateReducer.sortOrder);
  const templates = useSelector((state) => state.templateReducer.templates);
  const templatesPagination = useSelector((state) => state.templateReducer.templatesPagination);
  const [downloadingProof, setDownloadingProof] = useState({
    status: false,
    rowId: null,
  });

  const [downloadEnvelope, setDownloadingEnvelope] = useState({
    status: false,
    rowId: null,
  });

  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });

  const getAllTemplatesFromServer = useCallback(
    (page, perPage) => {
      dispatch(
        getAllTemplates(
          page,
          perPage,
          search,
          searchProductIds,
          searchCreator,
          '',
          selectedTemplateIds,
          true,
          false,
          true,
          sortBy,
          sortOrder
        )
      );
    },
    [dispatch, search, searchCreator, searchProductIds, selectedTemplateIds, sortBy, sortOrder]
  );
  const handleEditNavigate = useCallback(
    (id, templateType) => {
      if (templateType === 'json') {
        navigate(`/template-builder/${id}`);
      } else if (templateType === 'html_v2') {
        navigate(`/html-template-builder/${id}`);
      } else {
        navigate(`/template-html/${id}`);
      }
    },
    [navigate]
  );

  const handleRowClick = (params, event) => {
    event.stopPropagation();
    handleEditNavigate(params.row.id, params.row.templateType);
  };

  const handleChangePage = (event, page) => {
    getAllTemplatesFromServer(page, templates.perPage);
  };

  const handleShowDeleteDialog = (record) => {
    setOpenConfirmModal({
      type: 'delete',
      open: true,
      header: 'Delete Template',
      message: 'Are you sure you want to delete this Template?',
      description:
        'It will remain associated with existing orders but won’t be available for new orders.',
      handleOk: handleDelete,
      successButtonName: 'Yes',
      className: 'deleteTemplateModal',
      icon: ContactDetailsDeleteCIcon,
      handleClose: () => setOpenConfirmModal((prev) => ({ ...prev, open: false })),
    });
    setSelectedRecord(record);
  };

  const handleDuplicateDialog = (record) => {
    setIsShowDeleteModel((prev) => ({
      ...prev,
      open: true,
      type: 'duplicate',
    }));
    setSelectedRecord(record);
  };
  const handleDelete = async () => {
    try {
      setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
      const templateId = activeRowId?.id;
      if (!templateId) {
        dispatch(failure('Unable to delete template'));
        return;
      }

      const response = await deleteTemplate(templateId);
      if (response?.status === 200) {
        dispatch(success(response.data.message));
        getAllTemplatesFromServer(templates.currentPage - 1, templates.perPage);
      } else {
        dispatch(failure(response?.data?.message || 'Unable to delete template'));
      }
    } catch (error) {
      dispatch(failure(error?.message || 'Unable to delete template'));
    } finally {
      setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
    }
  };

  const createViewProof = async (title, id, type) => {
    try {
      setDownloadingProof({
        status: true,
        id: id,
      });
      const response = await getViewProof(id, type);
      const binaryData = atob(response.data.data.base64Pdf);

      if (response.status === 200) {
        // Create a Uint8Array from the binary data
        const uint8Array = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          uint8Array[i] = binaryData.charCodeAt(i);
        }

        // Create a Blob from the Uint8Array
        const blob = new Blob([uint8Array], { type: 'application/pdf' });

        // Create an Object URL for the Blob
        const url = URL.createObjectURL(blob);
        downloadPDF(title, url);
        dispatch(success('Mailer Proof generated successfully'));
        handleClose();
      } else {
        dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
      }
    } catch (error) {
      dispatch(error?.message);
    } finally {
      setDownloadingProof({
        status: false,
        id: null,
      });
    }
  };

  const downloadEnvelopeProof = async (title, id) => {
    try {
      setDownloadingEnvelope({
        status: true,
        id: id,
      });
      const payload = {
        templateId: id,
      };
      await dispatch(createEnvelopeProof(title, payload));
    } catch (error) {
      console.log(error);
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    } finally {
      setDownloadingEnvelope({
        status: false,
        id: null,
      });
    }
  };

  const downloadPDF = (title, url) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = title + '.pdf';

    // Append the link to the document
    document.body.appendChild(link);

    // Trigger a click on the link
    link.click();

    // Remove the link from the document
    document.body.removeChild(link);
  };

  const isTooltip = displayTooltip();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [activeRowId, setActiveRowId] = useState(null);
  const [showNoRows, setShowNoRows] = useState(false);

  const useScreen = useScreenWidth();
  const isTableLoading = isLoading || templatesPagination.loading;
  const shouldShowEmptyState = templates.rows.length === 0 && !isTableLoading && showNoRows;

  // Keep the loading-to-empty transition consistent with Campaigns. Without
  // this small delay MUI briefly paints the empty overlay between requests.
  useEffect(() => {
    let timeoutId;

    if (!isTableLoading && templates.rows.length === 0) {
      timeoutId = window.setTimeout(() => setShowNoRows(true), 300);
    } else {
      setShowNoRows(false);
    }

    return () => window.clearTimeout(timeoutId);
  }, [isTableLoading, templates.rows.length]);

  const classes = {
    root: 'tableGrid',
    main: 'tableGridMain',
    columnHeaders: 'tableGridHeaderWrapper',
    columnHeader: 'tableGridColumnHeader',
    columnHeaderTitle: 'tableGridColumnHeaderTitle',
    virtualScrollerContent: 'tableGridVirtualScrollerContent',
    virtualScrollerRenderZone: 'tableGridVirtualScrollerRenderZone',
    cell: 'tableGridCell',
    row: 'tableGridRow',
    columnHeaderTitleContainer: 'tableGridColumnHeaderTitleContainer',
  };

  const columns = [
    {
      field: 'templateName',
      headerName: 'Template Name',
      width: 250,
      sortable: true,
      sortKey: 'name',
      renderCell: (params) =>
        stringLength(`${params.row.title}`, 23) ? (
          <Tooltip
            title={
              <div>
                {params.row.title}
                <br />
                {params.row.id}
              </div>
            }
          >
            <Box className="tempValueWrapper">
              <Typography>{params.row.title}</Typography>
              <Typography>ID: {params.row.id}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="tempValueWrapper">
            <Typography>{params.row.title}</Typography>
            <Typography>ID: {params.row.id}</Typography>
          </Box>
        ),
    },
    {
      field: 'productType',
      headerName: 'Product Type',
      type: 'number',
      width: 250,
      sortable: true,
      flex: window.innerWidth >= 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Tooltip
            title={params.row.product.productType}
            disableHoverListener={params.row.product.productType.length > 25 ? false : true}
          >
            <Typography>{params.row.product.productType}</Typography>
            {params.row.product.productType === 'Postcards' && (
              <Typography>{params.row.product.paperSize}</Typography>
            )}
          </Tooltip>
        </Box>
      ),
    },
    // {
    //   field: 'templateType',
    //   headerName: 'Template Type',
    //   width: 250,
    //   sortable: false,
    //   flex: window.innerWidth >= 1500 ? 1 : 0,
    //   renderCell: (params) => (
    //     <Box className="addressWrapper">
    //       <Typography>
    //         {params.row.templateType === 'json'
    //           ? 'Template Builder'
    //           : params.row.templateType.toUpperCase()}
    //       </Typography>
    //     </Box>
    //   ),
    // },
    {
      field: 'usedInOrders',
      headerName: 'Used in Orders',
      width: 250,
      sortable: false,
      flex: window.innerWidth >= 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>
            {/* {params.row.templateType === 'json'
              ? 'Template Builder'
              : params.row.templateType.toUpperCase()} */}
            {params.row.numberOfOrders ? params.row.numberOfOrders : '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated Date',
      width: 250,
      sortable: true,
      flex: window.innerWidth >= 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{dateFormat(params.row.updatedAt)}</Typography>
        </Box>
      ),
    },
    {
      field: 'creator',
      headerName: 'Created By',
      width: 250,
      sortable: true,
      sortKey: 'createdBy',
      flex: window.innerWidth >= 1500 ? 1 : 0,
      renderCell: (params) =>
        stringLength(`${params.row.creator?.fullName}`) ? (
          <Tooltip title={params.row.creator?.fullName} disableHoverListener={isTooltip}>
            <Box className="addressWrapper">
              <Typography>{params.row.creator?.fullName}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="addressWrapper">
            <Typography>{params.row.creator?.fullName}</Typography>
          </Box>
        ),
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      width: 250,
      sortable: true,
      flex: window.innerWidth >= 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{dateFormat(params.row.createdAt)}</Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <>
          <Box>
            <IconButton
              aria-label={`Open actions for template ${params.row.title}`}
              id={`template-actions-${params.row.id}`}
              aria-controls={open && activeRowId?.id === params.row.id ? 'long-menu' : undefined}
              aria-expanded={open && activeRowId?.id === params.row.id ? 'true' : undefined}
              aria-haspopup="true"
              onClick={(event) => {
                event.stopPropagation();
                setActiveRowId(params.row); // Set active row ID when the menu is opened
                handleClick(event);
              }}
              sx={{
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <MoreVertIcon
                sx={{
                  opacity: activeRowId?.id === params.row.id && anchorEl ? 1 : 0.5,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              />
            </IconButton>
          </Box>
        </>
      ),
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [screenWidth]);

  // Server-side sorting: the sort lives in the URL (owned by the Templates page) and is mirrored in
  // Redux, so the grid always reflects it - including after refresh and back/forward navigation.
  const { sortProps } = useSortParams(columns, onSortChange, { sortBy, sortOrder });

  useEffect(() => {
    if (!isTableLoading && templates.rows.length === 0 && templates.currentPage > 1) {
      getAllTemplatesFromServer(templates.currentPage - 1, templates.perPage);
    }
  }, [
    getAllTemplatesFromServer,
    isTableLoading,
    templates.currentPage,
    templates.perPage,
    templates.rows,
  ]);

  return (
    <div
      style={{
        width: '100%',
      }}
      className="templateListingWrapper olcPrimaryListTable"
    >
      {isShowDeleteModel.open && isShowDeleteModel.type === 'delete' && (
        <DeleteTemplateModal
          open={isShowDeleteModel.open}
          handleClose={() => setIsShowDeleteModel(false)}
          handleDelete={handleDelete}
        />
      )}
      {isShowDeleteModel.open && isShowDeleteModel.type === 'duplicate' && (
        <DuplicateTemplateModal
          open={isShowDeleteModel.open}
          handleClose={setIsShowDeleteModel}
          selectedRecord={selectedRecord}
        />
      )}

      <DataGrid
        rows={templates.rows}
        columns={columns}
        shrinkColumnsToFit
        pageSizeOptions={[10, 20, 50, 100]}
        rowCount={templates.count}
        paginationMode="server"
        {...sortProps}
        hideFooterSelectedRowCount
        hideFooterPagination
        rowSelection={false}
        columnHeaderHeight={47}
        onRowClick={handleRowClick}
        getRowId={(row) => row?.id}
        disableColumnMenu={true}
        rowHeight={59}
        classes={{
          ...classes,
          virtualScroller: 'tableGridVirtualScroller',
        }}
        loading={isTableLoading}
        emptyState={{
          visible: showNoRows,
          message: getNoResultsMessage({ searchApplied, filterSize }),
        }}
      />
      {(isTableLoading || templates.count > 0) && (
        <OlcTablePagination
          count={Math.ceil(templates.count / templates.perPage)}
          page={templates.currentPage || 1}
          pageSize={templates.perPage}
          onPageChange={handleChangePage}
          onPageSizeChange={(event) => getAllTemplatesFromServer(1, event.target.value)}
          from={templates.from}
          to={templates.to}
          total={templates.count}
          loading={isTableLoading}
        />
      )}
      <OlcActionMenu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': activeRowId ? `template-actions-${activeRowId.id}` : undefined,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            handleDuplicateDialog(activeRowId);
            handleClose();
          }}
        >
          <img src={DuplicateTemplateCIcon} alt="duplicate" />
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            createViewProof(activeRowId?.title, activeRowId?.id, activeRowId?.templateType);
          }}
          disabled={downloadingProof.status && downloadingProof.id === activeRowId?.id}
        >
          {downloadingProof.status && downloadingProof.id === activeRowId?.id ? (
            <CircularProgress
              sx={{
                color: '#ed5c2f !important',
                width: '19px !important',
                height: '19px !important',
              }}
            />
          ) : (
            <img src={DownloadTemplateCIcon} alt="mailer proof" />
          )}
          {downloadingProof.status && downloadingProof.id === activeRowId?.id
            ? 'Downloading...'
            : 'Mailer Proof'}
        </MenuItem>
        {HAS_ENVELOPE.includes(+activeRowId?.product?.id) && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              downloadEnvelopeProof(activeRowId?.title, activeRowId?.id);
            }}
            disabled={downloadEnvelope.status && downloadEnvelope.id === activeRowId?.id}
          >
            {downloadEnvelope.status && downloadEnvelope.id === activeRowId?.id ? (
              <CircularProgress
                sx={{
                  color: '#ed5c2f !important',
                  width: '19px !important',
                  height: '19px !important',
                }}
              />
            ) : (
              <img src={DownloadTemplateCIcon} alt="envelope proof" />
            )}
            {downloadEnvelope.status && downloadEnvelope.id === activeRowId?.id
              ? 'Downloading...'
              : 'Envelope Proof'}
          </MenuItem>
        )}
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            handleShowDeleteDialog(activeRowId);
            handleClose();
          }}
        >
          <img src={TrashTemplateCIcon} alt="Delete" />
          Delete
        </MenuItem>
      </OlcActionMenu>

      <ConfirmModal openConfirmModal={openConfirmModal} setOpenConfirmModal={setOpenConfirmModal} />
    </div>
  );
};

export default TemplateListing;
